import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { palette } from '../../theme';
import { buildRig, type Rig, type LegBones } from '../../animation/rig';
import { solveTwoBoneIK, type Vec3 } from '../../animation/ik';
import {
  makeFootStepperState,
  updateFootStepper,
  type FootStepperState,
} from '../../animation/stepper';
import {
  makeSpringChain,
  updateSpringChain,
  type SpringChainState,
} from '../../animation/spring';
import { computeTailRestCurve } from '../../animation/tail';
import { breath, sumSines, twitch } from '../../animation/noise';
import { findPreset, ViewportGrid } from '../assets/presets';
import { RigVisual } from './RigVisual';
import { Inspector } from './Inspector';
import { defaultDesignerParams, type DesignerParams } from './params';

// ---------------------------------------------------------------------------
// Squirrel designer (v0). The procedural-animation sandbox.
//
// Driving systems: WASD body translation/heading, per-leg foot
// stepping, two-bone IK per leg, spring-coupled tail, spine lateral
// flex on turns, idle micro-noise. Inspector exposes ~22 sliders +
// show-debug toggle + reset. Rig proportions live-tunable via the
// inspector — changing them rebuilds the rig in place.
//
// Per the in-game-tooling spec, tools use plain React state for
// tool-local UI; no Koota.
// ---------------------------------------------------------------------------

export function SquirrelDesigner() {
  const [params, setParams] = useState<DesignerParams>(defaultDesignerParams);

  // Bumped by Reset rig (and Reset to defaults). Both feed rigKey, so
  // RigDriver remounts and its per-leg stepper / tail spring / heading
  // state reset to home. Bumping rebuilds the rig too, which puts
  // rig.root back at the world origin.
  const [resetEpoch, setResetEpoch] = useState(0);

  const rig = useMemo(
    () => buildRig(params.rig),
    [params.rig, resetEpoch],
  );
  const rigKey = useMemo(
    () => `${resetEpoch}:${JSON.stringify(params.rig)}`,
    [params.rig, resetEpoch],
  );
  const stage = findPreset('stage');

  function handleResetRig() {
    setResetEpoch(e => e + 1);
  }

  function handleResetDefaults() {
    setParams(defaultDesignerParams);
    setResetEpoch(e => e + 1);
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 320px',
        height: '100%',
        gap: 0,
        background: palette.barkBrown,
      }}
    >
      <div style={{ position: 'relative', height: '100%' }}>
        <Canvas
          shadows
          camera={{ position: [0.6, 0.4, 0.6], fov: 45 }}
          style={{ background: stage?.background ?? palette.softSkyBlue }}
        >
          {stage?.render()}
          <ViewportGrid />
          <RigDriver key={rigKey} rig={rig} params={params} />
          <RigVisual rig={rig} />
          <OrbitControls makeDefault target={[0, 0.15, 0]} />
        </Canvas>
        <Legend />
      </div>
      <Inspector
        params={params}
        setParams={setParams}
        onResetRig={handleResetRig}
        onResetDefaults={handleResetDefaults}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// RigDriver — runs every frame inside the Canvas. Consumes keyboard
// input, advances the body, runs per-leg stepping + IK, drives the
// tail spring, applies spine flex, and applies idle noise.
//
// Reads the latest params via a ref so per-frame logic always sees
// the current slider values without retriggering the React render.
// ---------------------------------------------------------------------------

function RigDriver({ rig, params }: { rig: Rig; params: DesignerParams }) {
  const keys = useKeyboard();

  // Latest params, mirrored into a ref for the per-frame loop.
  const paramsRef = useRef(params);
  paramsRef.current = params;

  // Per-leg stepper state.
  const stepperState = useRef<{
    legFL: FootStepperState;
    legFR: FootStepperState;
    legBL: FootStepperState;
    legBR: FootStepperState;
    initialised: boolean;
  }>({
    legFL: makeFootStepperState([0, 0, 0]),
    legFR: makeFootStepperState([0, 0, 0]),
    legBL: makeFootStepperState([0, 0, 0]),
    legBR: makeFootStepperState([0, 0, 0]),
    initialised: false,
  });

  const heading = useRef(0);
  const lastHeading = useRef(0);
  const smoothedHeadingRate = useRef(0);
  const lastPos = useRef(new THREE.Vector3());
  const tailChain = useRef<SpringChainState | null>(null);
  // Reusable buffers for the tail driver. Size matches tailSegments + 1
  // (root anchor + N segments). Reallocated when the rig remounts via
  // rigKey, which already triggers when tailSegments changes.
  const tailRestTargets = useRef<Array<[number, number, number]>>([]);
  const tailDampingPerNode = useRef<number[]>([]);
  // Self-accumulated time, in seconds. Avoids `state.clock` (THREE.Clock
  // is deprecated as of three r184 in favor of THREE.Timer). R3F still
  // constructs a Clock internally, so the deprecation warning fires
  // once on canvas mount, but our per-frame logic no longer depends on
  // it — we drive `t` from the same `dt` we already validate above.
  const t = useRef(0);

  const v1 = useMemo(() => new THREE.Vector3(), []);
  const v2 = useMemo(() => new THREE.Vector3(), []);
  const v3 = useMemo(() => new THREE.Vector3(), []);
  const bodyVelArr = useRef<Vec3>([0, 0, 0]);

  // Compute world matrices for the bone hierarchy once on mount. The
  // bones live under rig.root (an orphan THREE.Group not attached to
  // the scene), so the renderer never updates them automatically — we
  // have to do it ourselves before the first useFrame runs IK.
  useLayoutEffect(() => {
    rig.root.updateMatrixWorld(true);
  }, [rig]);

  useFrame((_state, dt) => {
    // THREE.Clock.getDelta() returns exactly 0 on its first call (the
    // auto-start branch). R3F calls it once per frame, so on the very
    // first frame after RigDriver mounts alongside the canvas, dt=0.
    // Dividing by stepDt below would yield 0/0 = NaN, which poisons
    // smoothedHeadingRate (NaN += anything === NaN) and from there
    // spine.rotation.y, permanently. That NaN spine rotation makes the
    // chest's matrixWorld translation NaN — every bone in the chest
    // subtree (chest, neck, head, ears, both front legs) ends up at
    // (NaN, NaN, NaN) in world space and never paints. Skip the frame
    // when dt isn't positive (also catches NaN dt).
    if (!(dt > 0)) return;
    const p = paramsRef.current;
    const stepDt = Math.min(dt, 0.05);
    t.current += stepDt;
    const tNow = t.current;

    if (!stepperState.current.initialised) {
      initFoot(rig.legFL, stepperState.current.legFL, v1);
      initFoot(rig.legFR, stepperState.current.legFR, v1);
      initFoot(rig.legBL, stepperState.current.legBL, v1);
      initFoot(rig.legBR, stepperState.current.legBR, v1);
      lastPos.current.copy(rig.root.position);

      // Initialise the tail spring chain on its rest curve, so it appears
      // already in carry pose instead of springing up from a flat trail.
      rig.hip.getWorldPosition(v1);
      const hipWorld: Vec3 = [v1.x, v1.y, v1.z];
      const N = rig.tail.length;
      const restBuf: Array<[number, number, number]> = new Array(N + 1);
      computeTailRestCurve(
        hipWorld,
        rig.root.rotation.y,
        0,
        {
          pitches: p.tail.pitches,
          segmentLength: p.rig.tailSegmentLength,
          segments: N,
          idleAmp: 0,
          idleFreq: 0,
          idleWaveCycles: 0,
          lateralBias: 0,
        },
        restBuf,
      );
      tailChain.current = makeSpringChain(restBuf);
      tailRestTargets.current = restBuf;
      tailDampingPerNode.current = new Array(N + 1).fill(p.tail.baseDamping);

      stepperState.current.initialised = true;
    }

    // Body driver. Tank-style: A/D pivot the squirrel; Q/E strafe.
    // Strafe is camera-confusing (depends on which way the squirrel
    // faces), so it's banished to the secondary keys we'll rarely use.
    let strafe = 0;
    let drive = 0;
    let turn = 0;
    if (keys.has('KeyW')) drive += 1;
    if (keys.has('KeyS')) drive -= 1;
    if (keys.has('KeyA')) turn += 1;     // A turns squirrel left (CCW from above)
    if (keys.has('KeyD')) turn -= 1;     // D turns squirrel right (CW)
    if (keys.has('KeyQ')) strafe += 1;   // Q strafes in the direction A turns
    if (keys.has('KeyE')) strafe -= 1;   // E strafes in the direction D turns

    heading.current += turn * p.movement.turnSpeed * stepDt;
    rig.root.rotation.y = heading.current;

    const cos = Math.cos(heading.current);
    const sin = Math.sin(heading.current);
    const dx = (sin * drive + cos * strafe) * p.movement.moveSpeed * stepDt;
    const dz = (cos * drive - sin * strafe) * p.movement.moveSpeed * stepDt;
    rig.root.position.x += dx;
    rig.root.position.z += dz;

    bodyVelArr.current = [
      (rig.root.position.x - lastPos.current.x) / stepDt,
      0,
      (rig.root.position.z - lastPos.current.z) / stepDt,
    ];
    lastPos.current.copy(rig.root.position);

    // Per-leg stepper + IK. Side determines pole direction (front knees
    // bend back, back knees bend forward) and which forward-offset to use
    // for the foot rest target (squirrels plant their hind paws ahead of
    // the hip).
    updateLeg(rig, rig.legFL, stepperState.current.legFL,
      !stepperState.current.legBR.lifting, 'front', bodyVelArr.current,
      stepDt, p, v1, v2, v3);
    updateLeg(rig, rig.legFR, stepperState.current.legFR,
      !stepperState.current.legBL.lifting, 'front', bodyVelArr.current,
      stepDt, p, v1, v2, v3);
    updateLeg(rig, rig.legBL, stepperState.current.legBL,
      !stepperState.current.legFR.lifting, 'back', bodyVelArr.current,
      stepDt, p, v1, v2, v3);
    updateLeg(rig, rig.legBR, stepperState.current.legBR,
      !stepperState.current.legFL.lifting, 'back', bodyVelArr.current,
      stepDt, p, v1, v2, v3);

    // Spine flex on turns.
    const headingRate = (heading.current - lastHeading.current) / stepDt;
    lastHeading.current = heading.current;
    const smoothing = Math.min(1, p.spine.smoothing * stepDt);
    smoothedHeadingRate.current +=
      (headingRate - smoothedHeadingRate.current) * smoothing;
    const bend = smoothedHeadingRate.current * p.spine.bendPerRate;
    rig.spine.rotation.y = bend;
    rig.chest.rotation.y = bend * 0.5;

    // Idle micro-noise. Head pitch composes with the rest pitch baked
    // into the rig — without the additive term, the override would wipe
    // the squirrel's looking-forward pose every frame.
    rig.head.rotation.y = sumSines(tNow, 1) * p.idle.headYawAmp;
    rig.head.rotation.x = p.rig.headRestPitch + sumSines(tNow, 2) * p.idle.headPitchAmp;
    rig.earL.root.rotation.x = twitch(tNow, 11, p.idle.earPeriod) * p.idle.earAmp;
    rig.earR.root.rotation.x = twitch(tNow, 12, p.idle.earPeriod) * p.idle.earAmp;
    rig.chest.position.y = breath(tNow, p.idle.breathRate) * p.idle.breathAmp;

    // Tail driver: rest-curve target + base-stiff/tip-floppy damping +
    // soft pull toward the carry shape, layered on top of the spring
    // dynamics. The tail is the loudest charm channel — without an
    // authored shape it just trails and looks dead.
    if (tailChain.current) {
      rig.hip.getWorldPosition(v1);
      const N = rig.tail.length;
      const hipWorld: Vec3 = [v1.x, v1.y, v1.z];

      // Refresh the rest-target buffer in place (no per-frame allocs).
      // Heading rate drives the lateral bias: tail's rest target slides
      // toward the outside of a turn while the body is rotating, then
      // decays back to centred. The spring chain pulls toward the
      // shifted target, so the visible tail swings during the turn and
      // settles back to its idle shape afterward.
      const lateralBias =
        smoothedHeadingRate.current * p.tail.turnResponse;
      computeTailRestCurve(
        hipWorld,
        rig.root.rotation.y,
        tNow,
        {
          pitches: p.tail.pitches,
          segmentLength: p.rig.tailSegmentLength,
          segments: N,
          idleAmp: p.tail.idleAmp,
          idleFreq: p.tail.idleFreq,
          idleWaveCycles: p.tail.idleWaveCycles,
          lateralBias,
        },
        tailRestTargets.current,
      );

      // Damping gradient: base stiff, tip floppy. Index 0 is the pinned
      // root and never used by the integrator, but filled for consistency.
      const damp = tailDampingPerNode.current;
      damp[0] = p.tail.baseDamping;
      for (let i = 1; i <= N; i++) {
        const t = N <= 1 ? 1 : i / N;
        damp[i] = p.tail.baseDamping + (p.tail.tipDamping - p.tail.baseDamping) * t;
      }

      updateSpringChain(
        tailChain.current,
        hipWorld,
        stepDt,
        {
          segmentLength: p.rig.tailSegmentLength,
          iterations: p.tail.iterations,
          damping: p.tail.baseDamping,
          dampingPerNode: damp,
          acceleration: [0, 0, 0],
          restTargets: tailRestTargets.current,
          restWeight: p.tail.restWeight,
        },
      );

      for (let i = 0; i < rig.tail.length; i++) {
        const bone = rig.tail[i];
        const parent = bone.parent!;
        parent.updateMatrixWorld(true);
        const node = tailChain.current.positions[i + 1];
        v2.set(node[0], node[1], node[2]);
        parent.worldToLocal(v2);
        bone.position.copy(v2);
      }
    }
  });

  // Debug overlay: foot-target gizmos.
  return params.showDebug ? <DebugOverlay stepper={stepperState.current} /> : null;
}

function initFoot(leg: LegBones, state: FootStepperState, scratch: THREE.Vector3) {
  leg.foot.getWorldPosition(scratch);
  const at: Vec3 = [scratch.x, 0, scratch.z];
  state.current = at;
  state.arcStart = at;
  state.arcEnd = at;
}

function updateLeg(
  rig: Rig,
  leg: LegBones,
  state: FootStepperState,
  diagonalGrounded: boolean,
  side: 'front' | 'back',
  bodyVel: Vec3,
  dt: number,
  params: DesignerParams,
  hipScratch: THREE.Vector3,
  kneeScratch: THREE.Vector3,
  footScratch: THREE.Vector3,
): void {
  leg.hip.getWorldPosition(hipScratch);

  // Foot rest target: the leg-hip's ground projection, plus a forward
  // offset in the body frame. Front and back legs use independent offsets
  // — squirrels plant their hind paws ahead of the hip joint, while the
  // front paws sit closer to directly under the shoulders.
  const headingY = rig.root.rotation.y;
  const forwardOffset =
    side === 'front'
      ? params.rig.frontFootForward
      : params.rig.backFootForward;
  const fwdSin = Math.sin(headingY);
  const fwdCos = Math.cos(headingY);
  const restWorld: Vec3 = [
    hipScratch.x + fwdSin * forwardOffset,
    0,
    hipScratch.z + fwdCos * forwardOffset,
  ];

  const stepperParams =
    side === 'front' ? params.frontStepper : params.backStepper;
  updateFootStepper(state, restWorld, bodyVel, dt, stepperParams, diagonalGrounded);

  // Pole hint: front knees bend backward, back knees bend forward.
  const poleSign = side === 'front' ? -1 : 1;
  const poleWorld: Vec3 = [
    hipScratch.x + poleSign * fwdSin,
    hipScratch.y,
    hipScratch.z + poleSign * fwdCos,
  ];

  const ik = solveTwoBoneIK(
    [hipScratch.x, hipScratch.y, hipScratch.z],
    state.current,
    poleWorld,
    leg.upperLength,
    leg.lowerLength,
  );

  kneeScratch.set(ik.kneePos[0], ik.kneePos[1], ik.kneePos[2]);
  leg.hip.worldToLocal(kneeScratch);
  leg.knee.position.copy(kneeScratch);

  leg.knee.updateMatrixWorld(true);
  footScratch.set(state.current[0], state.current[1], state.current[2]);
  leg.knee.worldToLocal(footScratch);
  leg.foot.position.copy(footScratch);
  leg.foot.updateMatrixWorld(true);
}

// ---------------------------------------------------------------------------
// Debug overlay — small gizmos that visualise hidden state. Only the
// foot-target spheres for v0 — pole-hint arrows and step-threshold
// rings can land later if the silhouette test demands them.
// ---------------------------------------------------------------------------

function DebugOverlay({
  stepper,
}: {
  stepper: {
    legFL: FootStepperState;
    legFR: FootStepperState;
    legBL: FootStepperState;
    legBR: FootStepperState;
  };
}) {
  const refFL = useRef<THREE.Mesh>(null);
  const refFR = useRef<THREE.Mesh>(null);
  const refBL = useRef<THREE.Mesh>(null);
  const refBR = useRef<THREE.Mesh>(null);

  useFrame(() => {
    syncTarget(refFL.current, stepper.legFL);
    syncTarget(refFR.current, stepper.legFR);
    syncTarget(refBL.current, stepper.legBL);
    syncTarget(refBR.current, stepper.legBR);
  });

  return (
    <group>
      <mesh ref={refFL}>
        <sphereGeometry args={[0.012, 8, 6]} />
        <meshBasicMaterial color={palette.goldenSun} />
      </mesh>
      <mesh ref={refFR}>
        <sphereGeometry args={[0.012, 8, 6]} />
        <meshBasicMaterial color={palette.goldenSun} />
      </mesh>
      <mesh ref={refBL}>
        <sphereGeometry args={[0.012, 8, 6]} />
        <meshBasicMaterial color={palette.goldenSun} />
      </mesh>
      <mesh ref={refBR}>
        <sphereGeometry args={[0.012, 8, 6]} />
        <meshBasicMaterial color={palette.goldenSun} />
      </mesh>
    </group>
  );
}

function syncTarget(mesh: THREE.Mesh | null, state: FootStepperState) {
  if (!mesh) return;
  mesh.position.set(state.current[0], state.current[1], state.current[2]);
}

// ---------------------------------------------------------------------------
// Keyboard tracking. Returns a Set of currently-pressed key codes.
// ---------------------------------------------------------------------------

function useKeyboard(): Set<string> {
  const keys = useRef<Set<string>>(new Set());

  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      keys.current.add(e.code);
    };
    const onUp = (e: KeyboardEvent) => {
      keys.current.delete(e.code);
    };
    const onBlur = () => {
      keys.current.clear();
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  return keys.current;
}

// ---------------------------------------------------------------------------
// Static UI bits.
// ---------------------------------------------------------------------------

function Legend() {
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 8,
        left: 8,
        background: 'rgba(0,0,0,0.55)',
        color: palette.warmBeige,
        padding: '6px 10px',
        borderRadius: 4,
        fontSize: 11,
        fontFamily: 'system-ui, sans-serif',
        pointerEvents: 'none',
      }}
    >
      <div>Squirrel designer · v0 · W/S move · A/D turn · Q/E strafe</div>
      <div style={{ opacity: 0.6, marginTop: 2 }}>
        Drag to orbit · wheel to zoom · click viewport first to focus input
      </div>
    </div>
  );
}
